/**
 * The console's session client: one WebSocket, one Noise session, one terminal.
 *
 * The protocol itself is not here. Framing and encryption live in `conduit-console`, compiled to
 * WebAssembly from the same Rust the endpoint runs — see that crate's module note for why a
 * TypeScript reimplementation was the wrong answer. This file is the plumbing around it: open the
 * socket, run the handshake, and move bytes between the terminal and the wire.
 *
 * # The order of operations is the security property
 *
 * 1. Connect, and send the **hello** in clear. The relay must read it to pair the two parties.
 * 2. Send the Noise handshake, and wait for the reply.
 * 3. Only then send or accept anything else.
 *
 * Step 3 is enforced by the WASM layer rather than by this file: `seal` refuses before the
 * handshake completes and `open` refuses a payload that arrives early. That placement is
 * deliberate — a rule enforced in the UI is a rule anybody can skip by writing their own client,
 * and the endpoint enforces the same one independently.
 *
 * # Where the endpoint's key comes from
 *
 * Not from here, and not from anything this file can choose. The caller passes the **client-signed
 * contract**, the WASM layer verifies the signature and extracts the endpoint's static key itself,
 * and `startHandshake` accepts nothing but the verified result.
 *
 * This used to be an `endpointStatic: Uint8Array` parameter with a comment saying it had to come
 * from the contract. The comment was correct and would not have stopped anybody: a later edit
 * reaching for a convenient field on an API response would have compiled, run, and connected — to
 * whichever endpoint the platform named. ADR-0020 is now a type rather than a note.
 */

import type { ConsoleFrame, ConsoleIdentity, ConsoleSession } from '../../wasm/conduit_console';

/**
 * The two channel identifiers this file names, matching `conduit_protocol::Channel::id`.
 *
 * Only these two, because only these two are things the console itself decides: it sends
 * keystrokes and it renders output. Clipboard, file transfer, control, and health are either
 * handled inside the WASM layer or not yet carried, and listing them here would be a second copy
 * of a mapping that already exists in Rust — which is how a paste ends up arriving as a keystroke.
 */
export const CHANNEL = {
  display: 2,
  input: 3,
} as const;

/** What the caller needs to open a session. */
export interface SessionParams {
  /** `wss://` URL of the relay's WebSocket listener. */
  relayUrl: string;
  /** The single-use rendezvous token for this session, 32 bytes. */
  rendezvousToken: Uint8Array;
  /**
   * The client-signed contract, exactly as `POST /api/v1/access-requests/{id}/session` served it.
   *
   * Passed through to the WASM layer unread. Nothing in TypeScript inspects these fields: a
   * `JSON.parse` here to pull out the endpoint key would be the whole attack, performed by our own
   * code with the best of intentions.
   */
  contract: ServedContract;
  /**
   * The identity this console generated **before** requesting the session, and the ticket the
   * platform signed over it (ADR-0021).
   *
   * Absent means the rendezvous is a bearer token — the platform issued no ticket, either because
   * this console sent no key or because the deployment signs none. The relay accepts that in its
   * open mode and refuses it in its enforcing one, so absence is a real state rather than an error
   * this file should invent.
   */
  ticket?: PresentedTicket;
}

/** The identity and the ticket that names it. */
export interface PresentedTicket {
  /**
   * The WASM keypair. Held by the page across the session request, because the public half has to
   * be sent before the ticket exists and the private half has to be here when it arrives.
   */
  identity: ConsoleIdentity;
  /** The session the ticket admits us to. */
  remoteSessionId: string;
  /** The key the platform bound, hex. Checked against the identity inside WASM. */
  consolePublicKey: string;
  /** When the ticket stops being presentable, RFC 3339. */
  expiresAt: string;
  /** The platform's signature, hex. */
  signature: string;
}

/** The contract envelope from the session API. */
export interface ServedContract {
  /** The contract document, as the client signed it. */
  document: unknown;
  /** The client's signature, 128 hex characters. */
  signatureHex: string;
  /** The tenant's pinned verifying key, 64 hex characters. */
  verifyingKeyHex: string;
}

/** What the caller wants told. */
export interface SessionHandlers {
  /** Decrypted target output, ready to write to the terminal. */
  onOutput: (bytes: Uint8Array) => void;
  /** The session became usable. */
  onSecured: () => void;
  /** The session ended, with a reason to show. */
  onClosed: (reason: string) => void;
  /** Something went wrong that the engineer needs to see. */
  onError: (error: Error) => void;
}

/**
 * A live console session.
 *
 * Created by {@link openSession}. Every method is safe to call after the session has closed — they
 * become no-ops rather than throwing, because a terminal component unmounting mid-teardown should
 * not produce an error the engineer sees on their way out.
 */
export class Session {
  #socket: WebSocket;
  #wasm: ConsoleSession;
  #closed = false;
  #handlers: SessionHandlers;

  constructor(socket: WebSocket, wasm: ConsoleSession, handlers: SessionHandlers) {
    this.#socket = socket;
    this.#wasm = wasm;
    this.#handlers = handlers;
  }

  /** Whether the Noise handshake has completed. */
  get secured(): boolean {
    return this.#wasm.isSecured;
  }

  /** Sends keystrokes to the target. */
  send(text: string): void {
    if (this.#closed) return;
    this.#emit(() => this.#wasm.seal(CHANNEL.input, 1, new TextEncoder().encode(text)));
  }

  /** Tells the endpoint the terminal was resized. */
  resize(cols: number, rows: number): void {
    if (this.#closed) return;
    // Bounded before it reaches the wire. The protocol carries these as u16, and a resize
    // observer can briefly report 0 while a panel is animating — which would open a PTY at 0x0 and
    // make some devices emit nothing at all.
    const c = Math.max(1, Math.min(cols, 65535));
    const r = Math.max(1, Math.min(rows, 65535));
    void import('../../wasm/conduit_console').then(({ sealResize }) => {
      this.#emit(() => sealResize(this.#wasm, c, r));
    });
  }

  /** Asks the endpoint to end the session. */
  close(): void {
    if (this.#closed) return;
    void import('../../wasm/conduit_console').then(({ sealClose }) => {
      this.#emit(() => sealClose(this.#wasm));
      // The endpoint tears down on its own; this is a request, not the teardown. The socket is
      // closed regardless, so a lost close frame does not leave the tab holding a connection.
      this.#socket.close();
    });
  }

  /** Marks the session closed and shuts the socket. */
  dispose(): void {
    this.#closed = true;
    if (this.#socket.readyState === WebSocket.OPEN) this.#socket.close();
  }

  /** Encodes, encrypts, and sends — reporting rather than throwing. */
  #emit(build: () => Uint8Array): void {
    try {
      this.#socket.send(build());
    } catch (err) {
      this.#handlers.onError(err instanceof Error ? err : new Error(String(err)));
    }
  }
}

/**
 * Opens a session: connects, runs the handshake, and starts pumping frames.
 *
 * Resolves once the Noise handshake has completed, so a caller that awaits it has a session it can
 * type into. It rejects if the handshake fails — which in `IK` means the responder does not hold
 * the private half of the key the contract named, and is therefore **not the endpoint the client
 * authorized**, whatever the platform routed us to.
 */
export async function openSession(
  params: SessionParams,
  handlers: SessionHandlers,
): Promise<Session> {
  const wasm = await import('../../wasm/conduit_console');
  await wasm.default();

  const socket = new WebSocket(params.relayUrl);
  socket.binaryType = 'arraybuffer';

  return new Promise<Session>((resolve, reject) => {
    let session: Session | undefined;
    let consoleSession: ConsoleSession | undefined;
    let settled = false;

    const fail = (error: Error): void => {
      if (!settled) {
        settled = true;
        reject(error);
      } else {
        handlers.onError(error);
      }
      socket.close();
    };

    socket.addEventListener('open', () => {
      try {
        // In clear, and first: the relay reads this to pair the two parties.
        //
        // Two forms, chosen by whether the platform issued a ticket. The ticketed one proves this
        // console holds the key the platform bound (ADR-0021); the plain one is a bearer token and
        // is what a deployment mid-rollout still uses. Neither carries the target, the contract, or
        // the tenant — see `conduit_protocol::hello`.
        socket.send(
          params.ticket
            ? wasm.ticketedHello(
                params.ticket.identity,
                params.rendezvousToken,
                params.ticket.remoteSessionId,
                params.ticket.consolePublicKey,
                // Seconds, not milliseconds, and a BigInt because the signature covers an i64.
                BigInt(Math.floor(Date.parse(params.ticket.expiresAt) / 1000)),
                params.ticket.signature,
              )
            : wasm.browserHello(params.rendezvousToken),
        );

        // Verified here, in WASM, against the client's signature — not parsed in TypeScript.
        // `verifyContract` is the only producer of the value `startHandshake` accepts, so a
        // console that skipped this step would not compile.
        //
        // The clock is the browser's, which the engineer controls. A wound-back clock accepts an
        // expired contract at this line and is refused by the endpoint, which checks the same
        // contract against its own. This check exists so the console can say "your contract
        // expired" rather than show a connection that fails for no stated reason.
        const verified = wasm.verifyContract(
          JSON.stringify(params.contract.document),
          params.contract.signatureHex,
          params.contract.verifyingKeyHex,
          BigInt(Math.floor(Date.now() / 1000)),
        );

        const started = wasm.ConsoleSession.startHandshake(verified);
        socket.send(wasm.handshakeFrame(started.message));
        consoleSession = started.intoSession();
      } catch (err) {
        fail(err instanceof Error ? err : new Error(String(err)));
      }
    });

    socket.addEventListener('message', (event: MessageEvent<ArrayBuffer>) => {
      const bytes = new Uint8Array(event.data);
      if (!consoleSession) return;

      try {
        // Before the handshake completes, the only thing expected is the reply. It is not
        // encrypted, so it does not go through `open` — which would refuse it, correctly.
        if (!consoleSession.isSecured) {
          // Parsed by the WASM layer, not here. A second reader of the frame header in
          // TypeScript would mean the channel identifier and control type written out again,
          // where they can drift from the Rust that defines them.
          const reply = wasm.handshakeReply(bytes);
          if (!reply) return;
          consoleSession.finishHandshake(reply);

          session = new Session(socket, consoleSession, handlers);
          settled = true;
          handlers.onSecured();
          resolve(session);
          return;
        }

        let frame: ConsoleFrame | undefined = consoleSession.open(bytes) ?? undefined;
        while (frame) {
          if (frame.channel === CHANNEL.display) handlers.onOutput(frame.payload);
          // More than one frame can arrive in a single message, so keep draining with no new
          // bytes until the buffer stops producing.
          frame = consoleSession.open(new Uint8Array()) ?? undefined;
        }
      } catch (err) {
        fail(err instanceof Error ? err : new Error(String(err)));
      }
    });

    socket.addEventListener('close', () => {
      session?.dispose();
      if (!settled) {
        settled = true;
        reject(new Error('the relay closed the connection before the session was established'));
      } else {
        handlers.onClosed('the session ended');
      }
    });

    socket.addEventListener('error', () => {
      fail(new Error('the connection to the relay failed'));
    });
  });
}

